import { useEffect, useRef, useState } from 'react'
import {
  ArrowDown, ArrowLeft, ArrowRight, ArrowUp, CheckCircle2, ChevronLeft, Download,
  Clapperboard, Home, Info, Library, Link2, ListVideo, Menu, MonitorPlay,
  Pause, Play, RefreshCw, RotateCcw, RotateCw, Search, Send, Server, Settings2,
  SkipBack, SkipForward, Smartphone, Sparkles, Square, Trash2, TriangleAlert, Tv, Unlink, X, XCircle,
} from 'lucide-react'
import { DEFAULT_SETTINGS } from '../constants'
import { useControllerRemote, type ControllerAck } from '../hooks/useControllerRemote'
import { formatBytesPerSecond, formatFileSize, formatTime, posterUrl } from '../lib/format'
import {
  getControllerProfiles,
  CONTROLLER_PROFILE_LIMIT,
  removeControllerProfile,
  rotateControllerToken,
  saveControllerProfile,
  submitPairingValues,
  unlinkController,
  updateControllerProfile,
  type ControllerProfile,
} from '../lib/pairing'
import { streamRemoteKey, toRemoteMediaRef, type RemoteCommand, type RemoteKey, type RemotePlayerState, type RemoteTvState } from '../lib/remoteProtocol'
import {
  getAddonCatalogPages,
  getAddonManifest,
  getCatalog,
  getMeta,
  getStreams,
  isHttpUrl,
  searchCatalog,
  type AddonCatalogDescriptor,
} from '../lib/stremio'
import { rankStreams } from '../lib/streams'
import { addonId, getStreamsFromAddons } from '../lib/addons'
import { readDiagnosticPayload, type PlaybackDiagnosticReport } from '../lib/diagnostics'
import { getPhoneSyncCarriers, savePhoneSyncCarrier, type PhoneSyncCarrier } from '../lib/sync'
import type { AddonConfig, AddonResource, Meta, MetaPreview, Video } from '../types'

type PhoneTab = 'browse' | 'search' | 'remote' | 'settings'
type PairState = { kind: 'idle' | 'sending' | 'ok' | 'error'; message?: string }

const PHONE_NAME = (() => {
  const match = navigator.userAgent.match(/(iPhone|iPad|Android)/i)
  return match?.[1] ? `${match[1]} phone` : 'Phone controller'
})()

function mediaRef(item: MetaPreview) {
  return toRemoteMediaRef(item)
}

function episodeLabel(video: Video, fallback: number) {
  return video.season && video.episode ? `S${video.season} E${video.episode}` : `Episode ${fallback}`
}

interface PhoneCatalog {
  key: string
  addon: AddonConfig
  catalog: AddonCatalogDescriptor
}

export function PhoneSetup() {
  const params = new URLSearchParams(window.location.search)
  const initialProfiles = getControllerProfiles()
  const [profiles, setProfiles] = useState(initialProfiles)
  const [profile, setProfile] = useState<ControllerProfile | null>(initialProfiles[0] || null)
  const [pairing, setPairing] = useState(Boolean(params.get('pair')) || !initialProfiles.length)
  const [code, setCode] = useState((params.get('pair') || '').toUpperCase())
  const [serverUrl, setServerUrl] = useState(DEFAULT_SETTINGS.serverUrl)
  const [torrentioUrl, setTorrentioUrl] = useState(DEFAULT_SETTINGS.torrentioUrl)
  const [pairState, setPairState] = useState<PairState>({ kind: 'idle' })
  const [tab, setTab] = useState<PhoneTab>('browse')
  const remote = useControllerRemote(profile)
  const diagnostic = params.get('diagnostic') ? readDiagnosticPayload(params.get('diagnostic')!) : null

  useEffect(() => {
    if (profile && remote.tvState?.sync) {
      savePhoneSyncCarrier(profile.deviceId, profile.localName || profile.deviceName, remote.tvState.sync)
    }
  }, [profile, remote.tvState?.sync])

  async function pair() {
    if (getControllerProfiles().length >= CONTROLLER_PROFILE_LIMIT) {
      setPairState({ kind: 'error', message: `This phone supports up to ${CONTROLLER_PROFILE_LIMIT} paired TVs. Remove one first.` })
      return
    }
    setPairState({ kind: 'sending' })
    try {
      const credentials = await submitPairingValues(code, { serverUrl, torrentioUrl }, PHONE_NAME)
      const next = saveControllerProfile(credentials, {
        serverUrl: credentials.serverUrl || serverUrl,
        torrentioUrl: credentials.torrentioUrl || torrentioUrl,
      })
      setProfiles(getControllerProfiles())
      setProfile(next)
      setPairState({ kind: 'ok', message: 'Paired forever. Opening your controller…' })
      window.setTimeout(() => { setPairing(false); setTab('remote') }, 500)
    } catch (error) {
      setPairState({ kind: 'error', message: error instanceof Error ? error.message : 'Could not reach the TV session.' })
    }
  }

  if (diagnostic) return <PhoneDiagnostic report={diagnostic} />

  if (pairing) {
    return (
      <PairPhone
        code={code}
        serverUrl={serverUrl}
        torrentioUrl={torrentioUrl}
        state={pairState}
        canCancel={Boolean(profile)}
        onCode={setCode}
        onServer={setServerUrl}
        onTorrentio={setTorrentioUrl}
        onPair={pair}
        onCancel={() => setPairing(false)}
      />
    )
  }

  if (!profile) {
    return <PairPhone code={code} serverUrl={serverUrl} torrentioUrl={torrentioUrl} state={pairState} canCancel={false} onCode={setCode} onServer={setServerUrl} onTorrentio={setTorrentioUrl} onPair={pair} onCancel={() => undefined} />
  }

  return (
    <main className="phone-remote">
      <header className="phone-remote-header">
        <span className="phone-mark"><Tv size="1.5rem" /></span>
        <div><strong>{profile.localName || profile.deviceName}</strong><small><i className={remote.tvOnline ? 'online' : ''} />{remote.status === 'connected' ? remote.tvOnline ? 'TV online' : 'TV offline · commands queue for 30 sec' : remote.status === 'unauthorized' ? 'Controller was unlinked' : 'Connecting…'}</small></div>
        <button onClick={() => remote.requestState()} aria-label="Refresh TV state"><RefreshCw size="1.25rem" /></button>
      </header>

      <section className="phone-remote-body">
        {tab === 'browse' ? <PhoneBrowse profile={profile} tvState={remote.tvState} send={remote.send} /> : null}
        {tab === 'search' ? <PhoneSearch profile={profile} tvState={remote.tvState} send={remote.send} /> : null}
        {tab === 'remote' ? <PhoneController state={remote.tvState} send={remote.send} /> : null}
        {tab === 'settings' ? (
          <PhoneRemoteSettings
            profile={profile}
            profiles={profiles}
            connection={remote.status}
            tvOnline={remote.status === 'connected' && remote.tvOnline}
            tvState={remote.tvState}
            send={remote.send}
            sendAndWait={remote.sendAndWait}
            onSelect={(value) => { setProfile(value); setTab('remote') }}
            onProfiles={(values) => { setProfiles(values); setProfile(values[0] || null) }}
            onProfile={(value) => { setProfile(value); setProfiles(getControllerProfiles()) }}
            onPair={() => {
              setCode('')
              setPairState(getControllerProfiles().length >= CONTROLLER_PROFILE_LIMIT
                ? { kind: 'error', message: `This phone supports up to ${CONTROLLER_PROFILE_LIMIT} paired TVs. Remove one first.` }
                : { kind: 'idle' })
              setPairing(true)
            }}
          />
        ) : null}
      </section>

      {remote.tvState?.player && tab !== 'remote' ? <MiniPlayer player={remote.tvState.player} send={remote.send} onOpen={() => setTab('remote')} /> : null}

      <nav className="phone-tabs">
        <PhoneTabButton active={tab === 'browse'} label="Browse" onClick={() => setTab('browse')}><Home size="1.5rem" /></PhoneTabButton>
        <PhoneTabButton active={tab === 'search'} label="Search" onClick={() => setTab('search')}><Search size="1.5rem" /></PhoneTabButton>
        <PhoneTabButton active={tab === 'remote'} label="Remote" onClick={() => setTab('remote')}><MonitorPlay size="1.5rem" /></PhoneTabButton>
        <PhoneTabButton active={tab === 'settings'} label="Settings" onClick={() => setTab('settings')}><Settings2 size="1.5rem" /></PhoneTabButton>
      </nav>
    </main>
  )
}

function PhoneDiagnostic({ report }: { report: PlaybackDiagnosticReport }) {
  function download() {
    const url = URL.createObjectURL(new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `vela-playback-report-${report.createdAt.replace(/[:.]/g, '-')}.json`
    link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
  }
  return <main className="phone-pair"><div className="phone-pair-card diagnostic-phone"><p className="phone-kicker">PRIVATE TV DIAGNOSTIC</p><h1>Playback report</h1><p>No media URL or viewing history left the TV. This report came inside the QR code.</p><div className="phone-diagnostic-grid"><span>TV model<strong>{report.tvModel}</strong></span><span>Media<strong>{report.media}</strong></span><span>Format<strong>{report.source.resolution} · {report.source.codec} · {report.source.audio}</strong></span><span>Container<strong>{report.source.container} · {report.source.hdr}</strong></span><span>Buffer<strong>{Math.round(report.playback.bufferedSeconds)} sec · {formatBytesPerSecond(report.playback.downloadSpeed)}</strong></span><span>Frames<strong>{report.playback.droppedFrames} dropped / {report.playback.totalFrames}</strong></span><span>Runtime<strong>{report.runtime}</strong></span>{report.playback.error ? <span>Error<strong>{report.playback.error}</strong></span> : null}</div><button className="phone-primary" onClick={download}><Download size="1.25rem" /> Save report</button></div></main>
}

function PairPhone({ code, serverUrl, torrentioUrl, state, canCancel, onCode, onServer, onTorrentio, onPair, onCancel }: {
  code: string
  serverUrl: string
  torrentioUrl: string
  state: PairState
  canCancel: boolean
  onCode: (value: string) => void
  onServer: (value: string) => void
  onTorrentio: (value: string) => void
  onPair: () => void
  onCancel: () => void
}) {
  return (
    <main className="phone-setup">
      <div className="phone-card">
        {canCancel ? <button className="phone-close" onClick={onCancel} aria-label="Close pairing"><X size="1.5rem" /></button> : null}
        <span className="phone-mark"><Link2 size="1.75rem" /></span>
        <p className="phone-kicker">VELA FOR LG WEBOS</p>
        <h1>Pair your phone</h1>
        <p>Enter the code shown on the TV. Separate private keys keep both devices linked until you unlink them.</p>

        <label><span>TV code</span><input value={code} maxLength={8} autoCapitalize="characters" autoComplete="one-time-code" onChange={(event) => onCode(event.target.value.replace(/[^a-z0-9]/gi, '').toUpperCase())} /></label>
        <details className="phone-advanced">
          <summary>Streaming addresses</summary>
          <label><span>Streaming server</span><input value={serverUrl} inputMode="url" onChange={(event) => onServer(event.target.value)} /></label>
          <label><span>Source add-on</span><input value={torrentioUrl} inputMode="url" onChange={(event) => onTorrentio(event.target.value)} /></label>
        </details>

        <button className="phone-primary" disabled={state.kind === 'sending' || code.length !== 8} onClick={onPair}><Send size="1.25rem" />{state.kind === 'sending' ? 'Pairing…' : 'Pair forever'}</button>
        {state.kind === 'ok' || state.kind === 'error' ? <div className={`phone-result ${state.kind}`}>{state.kind === 'ok' ? <CheckCircle2 size="1.5rem" /> : <XCircle size="1.5rem" />}<span>{state.message}</span></div> : null}
        <small>The linking code expires in ten minutes and works once. Remote keys remain only on this TV and phone; Vela stores no viewing history in the relay.</small>
      </div>
    </main>
  )
}

function PhoneBrowse({ profile, tvState, send }: { profile: ControllerProfile; tvState: RemoteTvState | null; send: (command: RemoteCommand) => string }) {
  const [kind, setKind] = useState<'movie' | 'series'>('movie')
  const [items, setItems] = useState<MetaPreview[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [detail, setDetail] = useState<Meta | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const detailToken = useRef(0)
  const [attempt, setAttempt] = useState(0)
  const [catalogs, setCatalogs] = useState<PhoneCatalog[]>([])
  const [catalogSource, setCatalogSource] = useState('cinemeta')
  const addonsKey = JSON.stringify(tvState?.settings?.addons || [])

  async function openDetail(item: MetaPreview) {
    const token = detailToken.current + 1
    detailToken.current = token
    setDetailLoading(true)
    try {
      const value = await getMeta(item.type, item.id, item.addonUrl)
      if (detailToken.current === token) setDetail(value)
    } catch {
      if (detailToken.current === token) setDetail({ ...item })
    } finally {
      if (detailToken.current === token) setDetailLoading(false)
    }
  }

  useEffect(() => () => { detailToken.current += 1 }, [])

  useEffect(() => {
    detailToken.current += 1
    setDetailLoading(false)
    let active = true
    setCatalogSource('cinemeta')
    setCatalogs([])
    const providers = (tvState?.settings?.addons || []).filter((addon) => (
      addon.enabled && (!addon.resources || addon.resources.includes('catalog'))
    ))
    if (!providers.length) return () => { active = false }
    Promise.allSettled(providers.map(async (addon) => ({ addon, manifest: await getAddonManifest(addon.url) })))
      .then((results) => active && setCatalogs(results.flatMap((result) => {
        if (result.status !== 'fulfilled') return []
        return (result.value.manifest.catalogs || [])
          .filter((catalog) => catalog.type === kind && catalog.id)
          .map((catalog) => ({
            key: `${result.value.addon.id}:${catalog.id}`,
            addon: result.value.addon,
            catalog,
          }))
      })))
    return () => { active = false }
    // The serialized key keeps frequent player-state frames from refetching
    // unchanged manifests while the phone browse tab remains open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addonsKey, kind])

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    setItems([])
    const selected = catalogs.find((entry) => entry.key === catalogSource)
    const request = selected
      ? getAddonCatalogPages(selected.addon.url, selected.catalog, {}, 3)
      : getCatalog(kind)
    request.then((value) => active && setItems(value)).catch(() => active && setError('Catalog unavailable.')).finally(() => active && setLoading(false))
    return () => { active = false }
  }, [attempt, catalogSource, catalogs, kind])

  if (detail) return <PhoneDetail meta={detail} profile={profile} tvState={tvState} send={send} onBack={() => setDetail(null)} />

  return (
    <div className="phone-page">
      <div className="phone-page-title"><div><small>BROWSE ON PHONE</small><h1>Choose on the couch</h1></div><button onClick={() => send({ kind: 'navigate', section: kind === 'movie' ? 'movies' : 'series' })}><Tv size="1.25rem" /> Show grid on TV</button></div>
      <div className="phone-segmented"><button className={kind === 'movie' ? 'active' : ''} onClick={() => setKind('movie')}>Movies</button><button className={kind === 'series' ? 'active' : ''} onClick={() => setKind('series')}>Series</button></div>
      {catalogs.length ? (
        <label className="phone-catalog-select"><span>Catalog</span><select aria-label="Catalog" value={catalogSource} onChange={(event) => setCatalogSource(event.target.value)}><option value="cinemeta">Cinemeta</option>{catalogs.map((entry) => <option value={entry.key} key={entry.key}>{entry.catalog.name || entry.catalog.id} · {entry.addon.name}</option>)}</select></label>
      ) : null}
      {loading ? <PhoneLoading /> : null}
      {detailLoading ? <PhoneLoading /> : null}
      {error ? <><PhoneError text={error} /><button className="phone-tv-action" onClick={() => setAttempt((value) => value + 1)}><RefreshCw size="1.25rem" /> Try again</button></> : null}
      <div className="phone-poster-grid">
        {items.map((item) => <PhonePoster key={`${item.type}:${item.id}`} item={item} onClick={() => openDetail(item)} />)}
      </div>
    </div>
  )
}

function PhoneSearch({ profile, tvState, send }: { profile: ControllerProfile; tvState: RemoteTvState | null; send: (command: RemoteCommand) => string }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<MetaPreview[]>([])
  const [detail, setDetail] = useState<Meta | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const detailToken = useRef(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)

  async function openDetail(item: MetaPreview) {
    const token = detailToken.current + 1
    detailToken.current = token
    setDetailLoading(true)
    try {
      const value = await getMeta(item.type, item.id, item.addonUrl)
      if (detailToken.current === token) setDetail(value)
    } catch {
      if (detailToken.current === token) setDetail({ ...item })
    } finally {
      if (detailToken.current === token) setDetailLoading(false)
    }
  }

  useEffect(() => () => { detailToken.current += 1 }, [])

  useEffect(() => {
    detailToken.current += 1
    setDetailLoading(false)
    if (query.trim().length < 2) { setResults([]); setError(''); setLoading(false); return }
    setResults([])
    setError('')
    setLoading(true)
    let active = true
    const timer = window.setTimeout(() => {
      searchCatalog(query)
        .then((value) => active && setResults(value))
        .catch(() => {
          if (active) { setResults([]); setError('Search unavailable. Check the connection and try again.') }
        })
        .finally(() => active && setLoading(false))
    }, 300)
    return () => { active = false; window.clearTimeout(timer) }
  }, [attempt, query])

  if (detail) return <PhoneDetail meta={detail} profile={profile} tvState={tvState} send={send} onBack={() => setDetail(null)} />

  return (
    <div className="phone-page">
      <div className="phone-page-title"><div><small>PHONE KEYBOARD</small><h1>Search Vela</h1></div></div>
      <div className="phone-search-input"><Search size="1.5rem" /><input autoFocus value={query} placeholder="Movies, series, actors…" onChange={(event) => setQuery(event.target.value)} /><button disabled={!query} onClick={() => setQuery('')}><X size="1.25rem" /></button></div>
      {query ? <button className="phone-tv-action" onClick={() => send({ kind: 'search', query })}><Tv size="1.25rem" /> Show this search on TV</button> : null}
      {loading ? <PhoneLoading /> : null}
      {detailLoading ? <PhoneLoading /> : null}
      {!loading && error ? <><PhoneError text={error} /><button className="phone-tv-action" onClick={() => setAttempt((value) => value + 1)}><RefreshCw size="1.25rem" /> Try again</button></> : null}
      {!loading && !error && query.length >= 2 && !results.length ? <PhoneError text="No matching titles." /> : null}
      <div className="phone-poster-grid">
        {results.map((item) => <PhonePoster key={`${item.type}:${item.id}`} item={item} onClick={() => openDetail(item)} />)}
      </div>
    </div>
  )
}

function PhoneDetail({ meta, profile, tvState, send, onBack }: { meta: Meta; profile: ControllerProfile; tvState: RemoteTvState | null; send: (command: RemoteCommand) => string; onBack: () => void }) {
  const [video, setVideo] = useState<Video | undefined>()
  const [showSources, setShowSources] = useState(false)
  const videos = (meta.videos || []).filter((item) => item.season !== 0)
  const seasons = Array.from(new Set(videos.map((item) => item.season).filter(Boolean)))
  const [season, setSeason] = useState(seasons[0])
  const saved = (tvState?.progress || []).find((entry) => entry.media.id === meta.id && entry.media.type === meta.type)
  const resumeVideo = saved?.videoId ? videos.find((item) => item.id === saved.videoId) : undefined
  const selectedVideo = video || (meta.type === 'series' ? resumeVideo || videos[0] : undefined)
  const inLibrary = (tvState?.library || []).some((item) => item.id === meta.id && item.type === meta.type)

  if (showSources) return <PhoneSources meta={meta} video={selectedVideo} profile={profile} tvState={tvState} send={send} onBack={() => setShowSources(false)} />

  return (
    <div className="phone-detail">
      <button className="phone-back" onClick={onBack}><ChevronLeft size="1.5rem" /> Back</button>
      <div className="phone-detail-hero" style={meta.background ? { backgroundImage: `url(${meta.background})` } : undefined}>
        <div><small>{meta.type === 'movie' ? 'MOVIE' : 'SERIES'} · {meta.releaseInfo || meta.year || ''}</small><h1>{meta.name}</h1>{meta.imdbRating ? <span>★ {meta.imdbRating}</span> : null}</div>
      </div>
      <p>{meta.description}</p>
      <div className="phone-detail-actions">
        <button className="primary" onClick={() => send({ kind: 'play_title', media: mediaRef(meta), videoId: selectedVideo?.id })}><Play size="1.25rem" fill="currentColor" /> {selectedVideo ? `${saved?.currentTime && selectedVideo.id === saved.videoId ? 'Resume' : 'Play'} ${episodeLabel(selectedVideo, 1)}` : saved?.currentTime ? 'Resume on TV' : 'Play on TV'}</button>
        <button onClick={() => send({ kind: 'open_title', media: mediaRef(meta) })}><Info size="1.25rem" /> Details on TV</button>
        <button onClick={() => setShowSources(true)}><ListVideo size="1.25rem" /> Sources</button>
        <button onClick={() => send({ kind: 'toggle_library', media: mediaRef(meta) })}><Library size="1.25rem" /> {inLibrary ? 'Remove from list' : 'Add to list'}</button>
      </div>
      {videos.length ? (
        <section className="phone-episodes">
          <div className="phone-segmented scroll">{seasons.map((value) => <button className={season === value ? 'active' : ''} onClick={() => setSeason(value)} key={value}>Season {value}</button>)}</div>
          {videos.filter((item) => item.season === season).map((item, index) => (
            <button className={selectedVideo?.id === item.id ? 'active' : ''} onClick={() => setVideo(item)} key={item.id}>
              {item.thumbnail ? <img src={item.thumbnail} alt="" /> : <span><Clapperboard size="1.5rem" /></span>}
              <div><small>{episodeLabel(item, index + 1)}</small><strong>{item.name || item.title || `Episode ${index + 1}`}</strong></div>
              <Play size="1.25rem" />
            </button>
          ))}
        </section>
      ) : null}
    </div>
  )
}

function PhoneSources({ meta, video, profile, tvState, send, onBack }: { meta: Meta; video?: Video; profile: ControllerProfile; tvState: RemoteTvState | null; send: (command: RemoteCommand) => string; onBack: () => void }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [streams, setStreams] = useState<Awaited<ReturnType<typeof rankStreams>>>([])
  const [attempt, setAttempt] = useState(0)
  const [visibleCount, setVisibleCount] = useState(24)
  const source = tvState?.sourceContext
  const addonUrl = source?.addonUrl || profile.torrentioUrl
  const codecKey = JSON.stringify(source?.codecCapabilities || null)
  const hdrKey = JSON.stringify(tvState?.hdrCapabilities || null)
  const settingsKey = JSON.stringify(tvState?.settings?.addons || null)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    setStreams([])
    setVisibleCount(24)
    if (source && !source.enabled) {
      setError('The source add-on is disabled on this TV.')
      setLoading(false)
      return () => { active = false }
    }
    (tvState?.settings ? getStreamsFromAddons(tvState.settings, meta.type, video?.id || meta.id) : getStreams(addonUrl, meta.type, video?.id || meta.id))
      .then((value) => {
        if (!active) return
        const ranked = rankStreams(value, source ? {
          runtime: meta.runtime,
          measuredSpeed: source.measuredSpeed,
          hdrMode: source.hdrMode,
          codecCapabilities: source.codecCapabilities,
          hdrCapabilities: { ...tvState!.hdrCapabilities, testedAt: 0 },
        } : {})
        setStreams(ranked)
        if (!ranked.length) setError('No sources were returned for this title.')
      })
      .catch(() => active && setError('Sources unavailable.'))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  // The serialized keys stand in for tvState/source: those objects arrive anew
  // with every player frame, and re-ranking sources on each one is pointless.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addonUrl, attempt, codecKey, hdrKey, meta.id, meta.type, settingsKey, source?.enabled, source?.hdrMode, source?.measuredSpeed, video?.id])

  return (
    <div className="phone-page phone-sources">
      <button className="phone-back" onClick={onBack}><ChevronLeft size="1.5rem" /> {meta.name}</button>
      <div className="phone-page-title"><div><small>{video ? episodeLabel(video, 1) : 'SOURCE CHOICE'}</small><h1>Choose the file</h1></div><button onClick={() => send({ kind: 'open_sources', media: mediaRef(meta), videoId: video?.id })}><Tv size="1.25rem" /> Show on TV</button></div>
      {loading ? <PhoneLoading /> : null}
      {error ? <><PhoneError text={error} />{source?.enabled !== false ? <button className="phone-tv-action" onClick={() => setAttempt((value) => value + 1)}><RefreshCw size="1.25rem" /> Try again</button> : null}</> : null}
      {streams.slice(0, visibleCount).map((choice, index) => (
        <button className={`phone-source${choice.policyBlocked ? ' blocked' : ''}`} disabled={!choice.allowed} onClick={() => send({ kind: 'play_source', media: mediaRef(meta), videoId: video?.id, sourceKey: streamRemoteKey(choice.stream) })} key={`${streamRemoteKey(choice.stream)}:${index}`}>
          <span className="quality">{choice.resolution.label}</span>
          <span><strong>{choice.policyBlocked ? 'Blocked by TV HDR setting' : index === 0 ? 'Best for this TV' : choice.stream.name || 'Source'}</strong><small>{[choice.codec && choice.codec.toUpperCase(), choice.container && choice.container.toUpperCase(), choice.audio && choice.audio.toUpperCase(), choice.hdr, formatFileSize(choice.size), choice.peers ? `${choice.peers} peers` : '', choice.evidence].filter(Boolean).join(' · ')}</small>{choice.risks.length ? <em>{choice.risks.join(' · ')}</em> : null}</span>
          {choice.allowed ? <Play size="1.5rem" /> : <TriangleAlert size="1.5rem" />}
        </button>
      ))}
      {streams.length > visibleCount ? <button className="phone-tv-action" onClick={() => setVisibleCount((value) => Math.min(streams.length, value + 24))}><ListVideo size="1.25rem" /> Show {Math.min(24, streams.length - visibleCount)} more · {streams.length - visibleCount} remaining</button> : null}
    </div>
  )
}

function PhoneController({ state, send }: { state: ReturnType<typeof useControllerRemote>['tvState']; send: (command: RemoteCommand) => string }) {
  const player = state?.player
  const trailer = !player && state?.screen === 'trailer'
  const episodeIndex = player ? player.episodes.findIndex((choice) => choice.active) : -1
  const touchStart = useRef<{ x: number; y: number; at: number } | null>(null)
  const sendKey = (key: RemoteKey) => send({ kind: 'key', key })

  function finishTouch(x: number, y: number) {
    const start = touchStart.current
    touchStart.current = null
    if (!start) return
    const dx = x - start.x
    const dy = y - start.y
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 24 && Date.now() - start.at < 500) { sendKey('ok'); return }
    if (Math.abs(dx) > Math.abs(dy)) sendKey(dx > 0 ? 'right' : 'left')
    else sendKey(dy > 0 ? 'down' : 'up')
  }

  return (
    <div className="phone-page phone-controller">
      {player ? (
        <section className="phone-now-playing">
          <div className="phone-now-art">{player.media.poster ? <img src={player.media.poster} alt="" /> : <MonitorPlay size="2.25rem" />}</div>
          <div><small>{player.label}{player.hdr.source ? ` · ${player.hdr.source}` : ' · SDR'}</small><h1>{player.title}</h1><p>{player.hdr.note}</p></div>
          <div className="phone-progress"><i style={{ width: `${player.duration ? player.currentTime / player.duration * 100 : 0}%` }} /></div>
          <span>{formatTime(player.currentTime)} / {formatTime(player.duration)}</span>
          <div className="phone-transport">
            <button aria-label="Stop playback" onClick={() => send({ kind: 'player', action: { kind: 'stop' } })}><Square size="1.25rem" fill="currentColor" /></button>
            <button aria-label="Previous episode" disabled={episodeIndex <= 0} onClick={() => send({ kind: 'player', action: { kind: 'previous' } })}><SkipBack size="1.5rem" /></button>
            <button aria-label="Back 10 seconds" onClick={() => send({ kind: 'player', action: { kind: 'seek_by', seconds: -10 } })}><RotateCcw size="1.5rem" /><small>10</small></button>
            <button className="play" aria-label={player.playing ? 'Pause playback' : 'Play'} onClick={() => send({ kind: 'player', action: { kind: 'toggle' } })}>{player.playing ? <Pause size="2rem" fill="currentColor" /> : <Play size="2rem" fill="currentColor" />}</button>
            <button aria-label="Forward 10 seconds" onClick={() => send({ kind: 'player', action: { kind: 'seek_by', seconds: 10 } })}><RotateCw size="1.5rem" /><small>10</small></button>
            <button aria-label="Next episode" disabled={episodeIndex < 0 || episodeIndex >= player.episodes.length - 1} onClick={() => send({ kind: 'player', action: { kind: 'next' } })}><SkipForward size="1.5rem" /></button>
          </div>
          {player.duration ? <PhoneSeek player={player} send={send} /> : null}
          <PlayerOptions player={player} send={send} />
        </section>
      ) : trailer ? (
        <section className="phone-now-playing phone-trailer-controls">
          <div className="phone-now-art"><Clapperboard size="2.25rem" /></div>
          <div><small>TRAILER ON TV</small><h1>{state?.selected?.name || 'Trailer'}</h1><p>LG transport controls work here too.</p></div>
          <div className="phone-transport">
            <button aria-label="Stop trailer" onClick={() => send({ kind: 'player', action: { kind: 'stop' } })}><Square size="1.25rem" fill="currentColor" /></button>
            <button aria-label="Back 30 seconds" onClick={() => send({ kind: 'player', action: { kind: 'seek_by', seconds: -30 } })}><RotateCcw size="1.5rem" /><small>30</small></button>
            <button className="play" aria-label="Play trailer" onClick={() => send({ kind: 'player', action: { kind: 'play' } })}><Play size="2rem" fill="currentColor" /></button>
            <button aria-label="Pause trailer" onClick={() => send({ kind: 'player', action: { kind: 'pause' } })}><Pause size="1.5rem" fill="currentColor" /></button>
            <button aria-label="Forward 30 seconds" onClick={() => send({ kind: 'player', action: { kind: 'seek_by', seconds: 30 } })}><RotateCw size="1.5rem" /><small>30</small></button>
          </div>
        </section>
      ) : (
        <div className="phone-tv-idle"><MonitorPlay size="2.75rem" /><h1>{state?.selected?.name || 'Vela is ready'}</h1><p>{state ? `TV screen: ${state.screen}` : 'Waiting for TV state…'}</p></div>
      )}

      <section className="magic-remote">
        <div className="lg-shortcuts"><button className="red" onClick={() => sendKey('red')} aria-label={player ? 'Sources shortcut' : 'Home shortcut'} /><button className="green" onClick={() => sendKey('green')} aria-label={player ? 'Subtitles shortcut' : 'Search shortcut'} /><button className="yellow" onClick={() => sendKey('yellow')} aria-label={player ? 'Audio shortcut' : 'Library shortcut'} /><button className="blue" onClick={() => sendKey('blue')} aria-label={player ? 'Diagnostics shortcut' : 'Settings shortcut'} /></div>
        <div
          className="magic-touchpad"
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId)
            touchStart.current = { x: event.clientX, y: event.clientY, at: Date.now() }
          }}
          onPointerUp={(event) => finishTouch(event.clientX, event.clientY)}
          onPointerCancel={() => { touchStart.current = null }}
        ><Sparkles size="1.75rem" /><strong>Swipe to move</strong><small>Tap for OK · Magic Remote style</small></div>
        <div className="phone-dpad">
          <button className="up" aria-label="Up" onClick={() => sendKey('up')}><ArrowUp /></button>
          <button className="left" aria-label="Left" onClick={() => sendKey('left')}><ArrowLeft /></button>
          <button className="ok" onClick={() => sendKey('ok')}>OK</button>
          <button className="right" aria-label="Right" onClick={() => sendKey('right')}><ArrowRight /></button>
          <button className="down" aria-label="Down" onClick={() => sendKey('down')}><ArrowDown /></button>
        </div>
        <div className="phone-system-keys"><button onClick={() => sendKey('back')}><ChevronLeft size="1.5rem" /> Back</button><button onClick={() => sendKey('home')}><Home size="1.5rem" /> Home</button><button onClick={() => sendKey('menu')}><Menu size="1.5rem" /> Settings</button></div>
      </section>
    </div>
  )
}

/**
 * A range input fires on every pointer move, and each one used to become a seek
 * on the TV. Drag against a local value and send once on release; the TV's own
 * position takes over again as soon as the drag ends, so the thumb never fights
 * the state frames arriving while a finger is down.
 */
function PhoneSeek({ player, send }: { player: RemotePlayerState; send: (command: RemoteCommand) => string }) {
  const [dragging, setDragging] = useState<number | null>(null)
  const position = dragging ?? Math.min(player.currentTime, player.duration)
  const commit = (seconds: number) => {
    setDragging(null)
    send({ kind: 'player', action: { kind: 'seek_to', seconds } })
  }
  return (
    <input
      className="phone-seek"
      aria-label="Seek"
      type="range"
      min={0}
      max={Math.floor(player.duration)}
      value={position}
      onChange={(event) => setDragging(Number(event.target.value))}
      onPointerUp={(event) => commit(Number((event.target as HTMLInputElement).value))}
      onKeyUp={(event) => commit(Number((event.target as HTMLInputElement).value))}
      onBlur={() => setDragging(null)}
    />
  )
}

function PlayerOptions({ player, send }: { player: RemotePlayerState; send: (command: RemoteCommand) => string }) {
  return (
    <div className="phone-player-options">
      <label><span>Subtitles</span><select value={player.subtitles.find((choice) => choice.active)?.id || 'off'} onChange={(event) => send({ kind: 'player', action: { kind: 'subtitle', id: event.target.value === 'off' ? null : event.target.value } })}>{player.subtitles.map((choice) => <option value={choice.id} key={choice.id}>{choice.label}</option>)}</select></label>
      <label><span>Audio</span><select disabled={!player.audio.length} value={player.audio.find((choice) => choice.active)?.id || ''} onChange={(event) => send({ kind: 'player', action: { kind: 'audio', index: Number(event.target.value) } })}>{player.audio.length ? player.audio.map((choice) => <option value={choice.id} key={choice.id}>{choice.label}</option>) : <option>Default</option>}</select></label>
      <label><span>Speed</span><select value={player.speed} onChange={(event) => send({ kind: 'player', action: { kind: 'speed', rate: Number(event.target.value) as 0.75 | 1 | 1.25 | 1.5 } })}>{[0.75, 1, 1.25, 1.5].map((rate) => <option value={rate} key={rate}>{rate}×</option>)}</select></label>
      <label><span>Picture</span><select value={player.picture} onChange={(event) => send({ kind: 'player', action: { kind: 'picture', mode: event.target.value as 'fit' | 'fill' | 'zoom' } })}><option value="fit">Fit</option><option value="fill">Fill</option><option value="zoom">Cinema zoom</option></select></label>
      <label><span>Sleep</span><select value={player.sleep} onChange={(event) => { const value = event.target.value; send({ kind: 'player', action: { kind: 'sleep', value: value === 'end' ? 'end' : Number(value) as 0 | 15 | 30 | 60 } }) }}><option value={0}>Off</option><option value={15}>15 min</option><option value={30}>30 min</option><option value={60}>60 min</option><option value="end">End of episode</option></select></label>
      {player.episodes.length > 1 ? <label className="phone-episode-choice"><span>Episode</span><select value={player.videoId || ''} onChange={(event) => send({ kind: 'play_title', media: mediaRef(player.media), videoId: event.target.value })}>{player.episodes.map((choice) => <option value={choice.id} key={choice.id}>{choice.label}{choice.detail ? ` · ${choice.detail}` : ''}</option>)}</select></label> : null}
    </div>
  )
}

function MiniPlayer({ player, send, onOpen }: { player: RemotePlayerState; send: (command: RemoteCommand) => string; onOpen: () => void }) {
  return <div className="phone-mini-player" onClick={onOpen}><div>{player.media.poster ? <img src={player.media.poster} alt="" /> : <Play />}</div><span><strong>{player.title}</strong><small>{formatTime(player.currentTime)} / {formatTime(player.duration)}</small></span><button aria-label={player.playing ? 'Pause playback' : 'Play'} onClick={(event) => { event.stopPropagation(); send({ kind: 'player', action: { kind: 'toggle' } }) }}>{player.playing ? <Pause fill="currentColor" /> : <Play fill="currentColor" />}</button></div>
}

function PhoneRemoteSettings({ profile, profiles, connection, tvOnline, tvState, send, sendAndWait, onSelect, onProfiles, onProfile, onPair }: {
  profile: ControllerProfile
  profiles: ControllerProfile[]
  connection: string
  tvOnline: boolean
  tvState: RemoteTvState | null
  send: (command: RemoteCommand) => string
  sendAndWait: (command: RemoteCommand, timeoutMilliseconds?: number) => Promise<ControllerAck>
  onSelect: (profile: ControllerProfile) => void
  onProfiles: (profiles: ControllerProfile[]) => void
  onProfile: (profile: ControllerProfile) => void
  onPair: () => void
}) {
  const [confirm, setConfirm] = useState(false)
  const [state, setState] = useState<PairState>({ kind: 'idle' })
  const [serverUrl, setServerUrl] = useState(tvState?.settings?.serverUrl || profile.serverUrl)
  const [torrentioUrl, setTorrentioUrl] = useState(tvState?.settings?.torrentioUrl || profile.torrentioUrl)
  const [localName, setLocalName] = useState(profile.localName || profile.deviceName)
  const [replaceSync, setReplaceSync] = useState('')
  const [manifestUrl, setManifestUrl] = useState('')
  const carriers = getPhoneSyncCarriers().filter((entry) => entry.deviceId !== profile.deviceId)

  useEffect(() => {
    setServerUrl(tvState?.settings?.serverUrl || profile.serverUrl)
    setTorrentioUrl(tvState?.settings?.torrentioUrl || profile.torrentioUrl)
    setLocalName(profile.localName || profile.deviceName)
  }, [profile.controllerId, profile.deviceName, profile.localName, profile.serverUrl, profile.torrentioUrl, tvState?.settings?.serverUrl, tvState?.settings?.torrentioUrl])

  function saveTvAddresses() {
    if (!isHttpUrl(serverUrl) || !isHttpUrl(torrentioUrl)) {
      setState({ kind: 'error', message: 'Enter valid HTTP or HTTPS addresses.' })
      return
    }
    send({ kind: 'update_settings', settings: { serverUrl, torrentioUrl } })
    onProfile(updateControllerProfile({ ...profile, serverUrl, torrentioUrl }))
    setState({ kind: 'ok', message: 'Addresses sent to TV.' })
  }

  async function rotate() {
    setState({ kind: 'sending', message: 'Rotating key…' })
    try {
      const next = await rotateControllerToken(profile)
      onProfile(next)
      setState({ kind: 'ok', message: 'Private controller key rotated.' })
    } catch (error) { setState({ kind: 'error', message: error instanceof Error ? error.message : 'Rotation failed.' }) }
  }

  async function unlink() {
    setState({ kind: 'sending', message: 'Unlinking…' })
    try {
      await unlinkController(profile)
      onProfiles(getControllerProfiles())
    } catch (error) {
      // If the server already revoked it, still allow removing the dead local key.
      const next = removeControllerProfile(profile)
      onProfiles(next)
      setState({ kind: 'error', message: error instanceof Error ? error.message : 'Remote key was already invalid.' })
    }
  }

  async function sendSync(carrier: PhoneSyncCarrier, mode: 'merge' | 'replace') {
    if (!tvState || !tvOnline) {
      setState({ kind: 'error', message: 'Target TV must be online to sync.' })
      return
    }
    setState({ kind: 'sending', message: 'Waiting for the live TV relay…' })
    try {
      const ack = await sendAndWait({ kind: 'apply_sync', snapshot: carrier.snapshot, mode })
      if (ack.status !== 'delivered') throw new Error(ack.message || (ack.status === 'rejected' ? 'TV rejected the sync.' : 'Sync was not delivered to the live TV.'))
      setReplaceSync('')
      setState({
        kind: 'ok',
        message: `${mode === 'merge' ? 'Merge' : 'Replace'} delivered from ${carrier.deviceName}.`,
      })
    } catch (error) {
      setState({ kind: 'error', message: error instanceof Error ? error.message : 'TV did not acknowledge the sync.' })
    }
  }

  function sendAddons(addons: NonNullable<RemoteTvState['settings']>['addons']) {
    if (addons.length > 12) {
      setState({ kind: 'error', message: 'Vela supports up to 12 add-on providers. Remove one first.' })
      return false
    }
    send({ kind: 'update_settings', settings: { addons: addons.map((addon, priority) => ({ ...addon, priority })) } })
    setState({ kind: 'ok', message: 'Add-on order sent to TV.' })
    return true
  }

  async function addManifest() {
    if (!isHttpUrl(manifestUrl) || !tvState?.settings) {
      setState({ kind: 'error', message: 'TV must be online and the manifest URL must be valid.' })
      return
    }
    const url = manifestUrl.trim().replace(/\/manifest\.json\/?$/i, '').replace(/\/+$/, '')
    const id = addonId(url)
    const replacing = tvState.settings.addons.some((addon) => addon.url === url || addon.id === id)
    if (!replacing && tvState.settings.addons.length >= 12) {
      setState({ kind: 'error', message: 'Vela supports up to 12 add-on providers. Remove one first.' })
      return
    }
    setState({ kind: 'sending', message: 'Reading add-on manifest…' })
    try {
      const manifest = await getAddonManifest(manifestUrl)
      const resources = (manifest.resources || []).map((resource) => typeof resource === 'string'
        ? resource
        : resource && typeof resource === 'object' && 'name' in resource && typeof resource.name === 'string' ? resource.name : '')
        .filter((resource): resource is AddonResource => ['catalog', 'meta', 'stream', 'subtitles'].includes(resource))
      const next = tvState.settings.addons.filter((addon) => addon.url !== url && addon.id !== id)
      next.push({
        id, name: manifest.name || new URL(url).hostname, url, enabled: true,
        priority: next.length, resources: resources.length ? resources : undefined,
      })
      if (!sendAddons(next)) return
      setManifestUrl('')
      setState({ kind: 'ok', message: `${manifest.name || 'Add-on'} added to TV.` })
    } catch (error) {
      setState({ kind: 'error', message: error instanceof Error ? error.message : 'Could not read manifest.' })
    }
  }

  return (
    <div className="phone-page phone-settings-page">
      <div className="phone-page-title"><div><small>PRIVATE CONTROLLERS</small><h1>Paired TVs</h1></div><button onClick={onPair}><Smartphone size="1.25rem" /> Add TV</button></div>
      <div className="phone-profile-list">{profiles.map((value) => <button className={value.controllerId === profile.controllerId ? 'active' : ''} onClick={() => onSelect(value)} key={`${value.deviceId}:${value.controllerId}`}><Tv size="1.5rem" /><span><strong>{value.localName || value.deviceName}</strong><small>{value.controllerId === profile.controllerId ? connection : 'Saved'}</small></span><ArrowRight size="1.25rem" /></button>)}</div>
      <section className="phone-settings-card phone-tv-settings">
        <h2>TV playback settings</h2>
        <p>Type long addresses here instead of with the LG remote. Changes use the private controller link.</p>
        <label><span>Streaming server</span><input inputMode="url" value={serverUrl} onChange={(event) => setServerUrl(event.target.value)} /></label>
        <label><span>Source add-on</span><input inputMode="url" value={torrentioUrl} onChange={(event) => setTorrentioUrl(event.target.value)} /></label>
        <button disabled={!isHttpUrl(serverUrl) || !isHttpUrl(torrentioUrl)} onClick={saveTvAddresses}><Send size="1.25rem" /> Send addresses to TV</button>
        {tvState?.settings ? (
          <div className="phone-tv-setting-choices">
            <button className={tvState.settings.torrentioEnabled ? 'active' : ''} onClick={() => send({ kind: 'update_settings', settings: { torrentioEnabled: !tvState.settings!.torrentioEnabled } })}>Sources {tvState.settings.torrentioEnabled ? 'on' : 'off'}</button>
            {(['auto', 'prefer', 'sdr'] as const).map((mode) => <button className={tvState.settings!.hdrMode === mode ? 'active' : ''} onClick={() => send({ kind: 'update_settings', settings: { hdrMode: mode } })} key={mode}>{mode === 'auto' ? 'HDR Auto' : mode === 'prefer' ? 'Prefer HDR' : 'SDR only'}</button>)}
          </div>
        ) : <small>TV settings appear when the TV is online.</small>}
      </section>
      <section className="phone-settings-card phone-tv-settings">
        <h2>TV add-ons</h2>
        <p>Add manifest URLs with the phone keyboard. Enable, reorder or remove every catalog, stream and subtitle provider.</p>
        {tvState?.settings ? <div className="phone-addon-list">{tvState.settings.addons.map((addon, index) => (
          <div key={addon.id}>
            <span><strong>{addon.name}</strong><small>{addon.resources?.join(' · ') || 'manifest resources'} · priority {index + 1}</small></span>
            <button className={addon.enabled ? 'active' : ''} onClick={() => sendAddons(tvState.settings!.addons.map((entry) => entry.id === addon.id ? { ...entry, enabled: !entry.enabled } : entry))}>{addon.enabled ? 'On' : 'Off'}</button>
            <button disabled={index === 0} aria-label={`Move ${addon.name} up`} onClick={() => { const next = [...tvState.settings!.addons]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; sendAddons(next) }}><ArrowUp size="1.25rem" /></button>
            <button disabled={index === tvState.settings!.addons.length - 1} aria-label={`Move ${addon.name} down`} onClick={() => { const next = [...tvState.settings!.addons]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; sendAddons(next) }}><ArrowDown size="1.25rem" /></button>
            <button className="danger" aria-label={`Remove ${addon.name}`} onClick={() => sendAddons(tvState.settings!.addons.filter((entry) => entry.id !== addon.id))}><Trash2 size="1.25rem" /></button>
          </div>
        ))}</div> : <small>TV must be online to manage its add-ons.</small>}
        <label><span>Add manifest URL</span><input inputMode="url" value={manifestUrl} onChange={(event) => setManifestUrl(event.target.value)} /></label>
        <button disabled={!tvState?.settings || !isHttpUrl(manifestUrl)} onClick={addManifest}><Send size="1.25rem" /> Add provider</button>
      </section>
      <section className="phone-settings-card phone-tv-settings">
        <h2>Sync paired TVs</h2>
        <p>Your phone privately carries the latest data from each TV. Pick another paired TV below; Vela never stores this snapshot in the relay.</p>
        {!carriers.length ? <small>Open another paired TV once. Its library and progress will then appear here.</small> : (
          <div className="phone-sync-list">
            {carriers.map((carrier) => (
              <div key={carrier.deviceId}>
                <span><strong>{carrier.deviceName}</strong><small>{carrier.snapshot.library.length} saved · {carrier.snapshot.progress.length} in progress · {new Date(carrier.capturedAt).toLocaleString()}</small></span>
                <button disabled={!tvOnline} onClick={() => sendSync(carrier, 'merge')}><RefreshCw size="1.25rem" /> Merge into this TV</button>
                {replaceSync !== carrier.deviceId
                  ? <button className="danger" disabled={!tvOnline} onClick={() => setReplaceSync(carrier.deviceId)}><TriangleAlert size="1.25rem" /> Replace this TV</button>
                  : <div className="phone-confirm"><button onClick={() => sendSync(carrier, 'replace')}><Trash2 size="1.25rem" /> Confirm replace</button><button onClick={() => setReplaceSync('')}>Cancel</button></div>}
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="phone-settings-card phone-tv-settings"><h2>Current controller</h2><p>Paired {new Date(profile.pairedAt).toLocaleDateString()} · key {profile.controllerId.slice(0, 6)}…</p><label><span>Name on this phone</span><input value={localName} maxLength={80} onChange={(event) => setLocalName(event.target.value)} /></label><button onClick={() => { const name = localName.trim(); if (name) onProfile(updateControllerProfile({ ...profile, localName: name })) }}><CheckCircle2 size="1.25rem" /> Save TV name</button><button onClick={rotate}><RefreshCw size="1.25rem" /> Rotate private key</button></section>
      <section className="phone-settings-card danger"><h2>Unlink this phone</h2><p>This removes only this phone. Other paired phones keep working.</p>{!confirm ? <button onClick={() => setConfirm(true)}><Unlink size="1.25rem" /> Unlink</button> : <div className="phone-confirm"><button onClick={unlink}><Trash2 size="1.25rem" /> Yes, unlink</button><button onClick={() => setConfirm(false)}>Cancel</button></div>}</section>
      {state.kind !== 'idle' ? <div className={`phone-result ${state.kind}`}>{state.kind === 'ok' ? <CheckCircle2 /> : state.kind === 'error' ? <XCircle /> : <RefreshCw className="spin" />} {state.message}</div> : null}
      <p className="phone-privacy-note"><Server size="1.25rem" /> The relay stores controller key hashes and a short offline command queue. Catalogs, history and playback state are not retained.</p>
    </div>
  )
}

function PhonePoster({ item, onClick }: { item: MetaPreview; onClick: () => void }) {
  return <button className="phone-poster" onClick={onClick}>{item.poster ? <img src={posterUrl(item.poster)} alt="" loading="lazy" /> : <span><Clapperboard /></span>}<strong>{item.name}</strong><small>{item.releaseInfo || item.year}</small></button>
}

function PhoneTabButton({ active, label, onClick, children }: { active: boolean; label: string; onClick: () => void; children: React.ReactNode }) {
  return <button className={active ? 'active' : ''} onClick={onClick}>{children}<span>{label}</span></button>
}

function PhoneLoading() {
  return <div className="phone-loading"><RefreshCw className="spin" /><span>Loading…</span></div>
}

function PhoneError({ text }: { text: string }) {
  return <div className="phone-error"><XCircle /><span>{text}</span></div>
}
